import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AdminProvider, useAdmin } from "@/contexts/AdminContext";
import { CustomerProvider, useCustomer } from "@/contexts/CustomerContext";
import { ConfigProvider } from "@/contexts/ConfigContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SidebarToggleProvider } from "@/contexts/SidebarToggleContext";
import AdminFooter from "@/components/admin/AdminFooter";
import useLangSync from "@/i18n/useLangSync";   // v2.7.45+E9 · syncs <html lang>
import { getSubdomain } from "@/lib/subdomain";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import NewTripPage from "@/pages/NewTripPage";
import CityRoutePage from "@/pages/CityRoutePage";
import CustomTripPage from "@/pages/CustomTripPage";
import MeterPage from "@/pages/MeterPage";
import DriverConsolePage from "@/pages/DriverConsolePage";
import TripsPage from "@/pages/TripsPage";
import FleetOptionsPage from "@/pages/FleetOptionsPage";
import DriverProfilePage from "@/pages/DriverProfilePage";
import EndOfDayPage from "@/pages/EndOfDayPage";
import DeliveryActivePage from "@/pages/DeliveryActivePage";
import DriverEarningsPage from "@/pages/DriverEarningsPage";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AshleyChatWidget from "@/components/admin/AshleyChatWidget";
import EmergencyWeatherBanner from "@/components/admin/EmergencyWeatherBanner";
import AudioActivityIndicator from "@/components/admin/AudioActivityIndicator";
import AdminOpsProvider from "@/components/admin/AdminOpsProvider";
import ApiVaultPage from "@/pages/admin/ApiVaultPage";
import CalendarPage from "@/pages/admin/CalendarPage";
import FutureFaresPage from "@/pages/admin/FutureFaresPage";
import KnowledgeCenterPage from "@/pages/admin/KnowledgeCenterPage";
import AppManagementPortalPage from "@/pages/admin/AppManagementPortalPage";  // v2.7.65+E128
import DriverAppManagerPage from "@/pages/admin/DriverAppManagerPage";  // v2.7.65+E129
import VoucherProgramPage from "@/pages/marketing/VoucherProgramPage";  // v2.7.65+E132
import VoucherLedgerPage  from "@/pages/marketing/VoucherLedgerPage";   // v2.7.65+E133
import VoucherAdminPage from "@/pages/admin/VoucherAdminPage";  // v2.7.65+E132
import ManageEmailsPage from "@/pages/admin/ManageEmailsPage";  // v2.7.65+E138
import RateTableAdminPage from "@/pages/admin/RateTableAdminPage";  // v2.7.65+E144
import ReceiptPage from "@/pages/ReceiptPage";  // v2.7.65+E145 · public receipt permalink
import QueuedRidesPage from "@/pages/QueuedRidesPage";  // v2.7.65+E147
import FutureTripsPage from "@/pages/FutureTripsPage";  // v2.7.65+E216
import RideCoordinatorPage from "@/pages/admin/RideCoordinatorPage";  // v2.7.65+E147
import RiderHoursPage from "@/pages/admin/RiderHoursPage";  // v2.8+E301
import CallerMatchPage from "@/pages/admin/CallerMatchPage";  // v2.8+E337 · Dialpad screen-pop
import DialpadManagerPage from "@/pages/admin/DialpadManagerPage";  // v2.8+E338 · Dialpad Manager
import TimeSheetsPage from "@/pages/admin/TimeSheetsPage";  // v2.8+E342 · Timesheets
import SchedulePage from "@/pages/admin/SchedulePage";  // v2.8+E342 · Dispatcher Schedule
import AdminGate from "@/components/admin/AdminGate";
import PortalPage from "@/pages/portal/PortalPage";
import AuthCallback from "@/pages/AuthCallback";
import AuthDriverCallback from "@/pages/AuthDriverCallback";
import AuthAdminCallback from "@/pages/AuthAdminCallback";
import AuthApplyCallback from "@/pages/AuthApplyCallback";
import AuthMagicCallback from "@/pages/AuthMagicCallback";
import CustomerLoginPage from "@/pages/customer/CustomerLoginPage";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import LegalPage from "@/pages/LegalPage";
import DriverVerificationPage from "@/pages/DriverVerificationPage";
import IncidentReportPage from "@/pages/IncidentReportPage";
import MarketingHome from "@/pages/marketing/MarketingHome";
import GroupRidesPage from "@/pages/marketing/GroupRidesPage";  // v2.7.65+E238
import LoyaltyProgramPage from "@/pages/marketing/LoyaltyProgramPage";  // v2.7.65+E257
import QrPosterPage from "@/pages/marketing/QrPosterPage";  // v2.7.65+E264
import { MarketingAbout, MarketingServices, MarketingVehicles, MarketingRates, MarketingContact } from "@/pages/marketing/MarketingPages";
import MarketingDelivery from "@/pages/marketing/MarketingDelivery";
import MarketingTourism from "@/pages/marketing/MarketingTourism";
import MarketingTourismCity from "@/pages/marketing/MarketingTourismCity";
import OurTeamPage from "@/pages/marketing/OurTeamPage";  // v2.7.65+E239
import MarketingBisonTaxiCommander from "@/pages/marketing/MarketingBisonTaxiCommander";
import MarketingDriveWithUs from "@/pages/marketing/MarketingDriveWithUs";
import ContractorSetupPage from "@/pages/ContractorSetupPage";
import PageGate from "@/components/PageGate";
import DevLicensePage from "@/pages/admin/DevLicensePage";
import SystemHealthPage from "@/pages/admin/SystemHealthPage";
import WeatherCommanderPage from "@/pages/admin/WeatherCommanderPage";
import StateEmergenciesPage from "@/pages/admin/StateEmergenciesPage";
import DriverSafetyPage from "@/pages/admin/DriverSafetyPage";
// v2.8+E314 · HosAuditPage + DriverTimeControlPage imports removed — the DOT
// Compliance System is disabled; their routes now redirect to the dashboard.
import ActivePowerOutagesPage from "@/pages/admin/ActivePowerOutagesPage";
import WeatherCMSPage from "@/pages/admin/WeatherCMSPage";
import LocalWeatherPage from "@/pages/LocalWeatherPage";
import DispatchVideoWallPage from "@/pages/admin/DispatchVideoWallPage";
import KioskLinksPage from "@/pages/admin/KioskLinksPage";
import PaletteDebugPage from "@/pages/admin/PaletteDebugPage";
import EmailClientPage from "@/pages/admin/EmailClientPage";
import DeveloperEmailAdminPage from "@/pages/admin/DeveloperEmailAdminPage";
import StaffChatPanel from "@/pages/admin/StaffChatPanel";
import HelpdeskPage from "@/pages/admin/HelpdeskPage";
import ConsoleGridPage from "@/pages/admin/ConsoleGridPage";
import LiveMapOnlyPage from "@/pages/admin/LiveMapOnlyPage";
// Update 4 · Detached TAXI Calculator pop-out (calculator-only, AdminProtected)
import TaxiCalculatorPopupPage from "@/pages/admin/TaxiCalculatorPopupPage";
import ConsoleSessionsPage from "@/pages/admin/ConsoleSessionsPage";
import MasterAccessPage from "@/pages/admin/MasterAccessPage";  // v2.7.50+E26 Phase 1
import DropboxPage from "@/pages/admin/DropboxPage";  // v2.7.51+E27 Storage Portal Phase 2
import TrainingWikiPage from "@/pages/admin/training/TrainingWikiPage";  // v2.7.64+E49 Training Center
import TrainingLmsPage from "@/pages/admin/training/TrainingLmsPage";
import TrainingKmcPage from "@/pages/admin/training/TrainingKmcPage";
import KioskWallPage from "@/pages/KioskWallPage";
import NewContractPage from "@/pages/admin/contracts/NewContractPage";
import ContractsListPage from "@/pages/admin/contracts/ContractsListPage";
import ContractDetailPage from "@/pages/admin/contracts/ContractDetailPage";
import CookieConsentBanner from "@/components/CookieConsentBanner";

const Protected = ({ children }) => {
  const { driver, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0c0d10] text-amber-400">Loading…</div>;
  if (!driver) return <Navigate to="/login" replace />;
  return children;
};
const AdminProtected = ({ children }) => {
  const { admin } = useAdmin();
  if (!admin) return <Navigate to="/admin/login" replace />;
  return (
    <AdminOpsProvider>
      {children}
      {/* v2.7.65+E99 · Ashley AI assistant · lower-right floating widget,
          gated internally to admin/supervisor/dispatcher/team_leader roles. */}
      <AshleyChatWidget/>
      {/* v2.7.65+E213 · proactive severe-weather banner for all logged-in staff */}
      <EmergencyWeatherBanner/>
      {/* v2.7.65+E219 · persistent audio play-state indicator + master stop */}
      <AudioActivityIndicator/>
    </AdminOpsProvider>
  );
};
const CustomerProtected = ({ children }) => {
  const { customer } = useCustomer();
  if (!customer) return <Navigate to="/ride/login" replace />;
  return children;
};

/**
 * v2.6.7 batch D · Subdomain-aware root component.
 * v2.6.7 batch I · Portal hidden — `portal` subdomain falls through to admin.
 * v2.6.9 batch BQ · `/` now serves the public marketing site. Driver
 * dashboard moved to `/dashboard`. Subdomain shortcuts still go directly
 * to their respective portals.
 */
const RootRoute = () => {
  const sub = getSubdomain();
  if (sub === "ride")   return <Navigate to="/ride" replace />;
  if (sub === "admin")  return <Navigate to="/admin" replace />;
  if (sub === "portal") return <Navigate to="/admin" replace />;
  return <MarketingHome />;
};

// v2.6.7 batch I · PORTAL is feature-flagged OFF for now. Flip this to true
// when the live banking integrations (Mercury/Plaid/Square Banking) are ready
// and the user wants the accounting portal exposed again.
const PORTAL_ENABLED = false;

function AppShell() {
  // v2.7.45+E9 · keep <html lang>/<dir> aligned with i18next active language
  useLangSync();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/receipt/:tripId" element={<ReceiptPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/driver-callback" element={<AuthDriverCallback />} />
        <Route path="/auth/admin-callback" element={<AuthAdminCallback />} />
        <Route path="/auth/apply-callback" element={<AuthApplyCallback />} />
        <Route path="/auth/magic" element={<AuthMagicCallback />} />
        <Route path="/" element={<RootRoute />} />
        {/* v2.6.9 batch BQ · public marketing pages (v2.7.0 batch DI gated by admin Page Visibility) */}
        <Route path="/about"    element={<PageGate pageKey="about"><MarketingAbout/></PageGate>} />
        {/* v2.7.65+E239 · Our Team page (replaces /leadership + standalone
            /drivers; old links redirect here). */}
        <Route path="/our-team" element={<OurTeamPage/>} />
        <Route path="/leadership" element={<Navigate to="/our-team" replace/>} />
        <Route path="/leadership/:id" element={<Navigate to="/our-team" replace/>} />
        <Route path="/drivers" element={<Navigate to="/our-team" replace/>} />
        <Route path="/drivers/:id" element={<Navigate to="/our-team" replace/>} />
        <Route path="/bison-taxi-commander" element={<MarketingBisonTaxiCommander/>} />
        <Route path="/services" element={<PageGate pageKey="services"><MarketingServices/></PageGate>} />
        <Route path="/vehicles" element={<PageGate pageKey="vehicles"><MarketingVehicles/></PageGate>} />
        <Route path="/rates"    element={<MarketingRates/>} />
        <Route path="/group-rides" element={<GroupRidesPage/>} />{/* v2.7.65+E238 */}
        <Route path="/loyalty" element={<LoyaltyProgramPage/>} />{/* v2.7.65+E257 */}
        <Route path="/qr-poster" element={<QrPosterPage/>} />{/* v2.7.65+E264 */}
        {/* v2.7.57+E34 · DELIVERY SYSTEM · Section 1 · Public multi-stop
            intake form. Public — no auth. Sections 2-5 will bolt onto
            this route (quote engine, admin dispatch board, driver flow). */}
        <Route path="/delivery" element={<MarketingDelivery/>} />
        <Route path="/tourism"  element={<PageGate pageKey="tourism"><MarketingTourism/></PageGate>} />
        <Route path="/tourism/:slug" element={<PageGate pageKey="tourism"><MarketingTourismCity/></PageGate>} />
        <Route path="/contact"  element={<PageGate pageKey="contact"><MarketingContact/></PageGate>} />
        {/* v2.7.65+E80 · Public "Drive For Us" application intake · v2.7.65+E77 route kept as alias */}
        <Route path="/drive-for-us"  element={<MarketingDriveWithUs/>} />
        <Route path="/drive-with-us" element={<MarketingDriveWithUs/>} />
        {/* v2.7.65+E77 · Private (unlinked) contractor-setup stub */}
        <Route path="/onboarding/contractor-setup" element={<ContractorSetupPage/>} />
        {/* v2.6.9 batch BQ · driver dashboard moved to /dashboard (was /). */}
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        {/* v2.8+E337 · Dialpad screen-pop landing (admin/dispatcher only). */}
        <Route path="/dashboard/caller-match" element={<AdminProtected><CallerMatchPage /></AdminProtected>} />
        {/* v2.8+E338 · Dialpad Manager (super/admin gated; backend enforces). */}
        <Route path="/admin/dialpad-manager" element={<AdminProtected><DialpadManagerPage /></AdminProtected>} />
        {/* v2.8+E342 · Time Clock / Timesheets / Schedule (role gated; backend enforces). */}
        <Route path="/admin/time-sheets" element={<AdminProtected><TimeSheetsPage /></AdminProtected>} />
        <Route path="/admin/schedule" element={<AdminProtected><SchedulePage /></AdminProtected>} />
        {/* v2.7.65+E132 · Driver App Deployment Isolation.
            The Lyft-style Driver Console (/drive · v2.7.65+E126) is behind a
            feature flag so it can NEVER ship to production before it's fully
            green-lit. When REACT_APP_ENABLE_NEW_DRIVER_UI is falsy (default),
            /drive silently redirects to the stable legacy dashboard so any
            active driver keeps working without disruption. Flip the env var
            to "true" only when the redesign is cleared for release. */}
        <Route
          path="/drive"
          element={
            String(process.env.REACT_APP_ENABLE_NEW_DRIVER_UI || "").toLowerCase() === "true"
              ? <Protected><DriverConsolePage /></Protected>
              : <Navigate to="/dashboard" replace/>
          }
        />
        {/* /group removed v2.6.5 — group ride feature retired */}
        <Route path="/city" element={<Protected><CityRoutePage /></Protected>} />
        <Route path="/new-trip" element={<Protected><NewTripPage /></Protected>} />
        <Route path="/queue" element={<Protected><QueuedRidesPage /></Protected>} />
        <Route path="/future-trips" element={<Protected><FutureTripsPage /></Protected>} />
        <Route path="/custom" element={<Protected><CustomTripPage /></Protected>} />
        <Route path="/meter" element={<Protected><MeterPage variant="weatherford" /></Protected>} />
        <Route path="/meter-statewide" element={<Protected><MeterPage variant="statewide" /></Protected>} />
        <Route path="/trips" element={<Protected><TripsPage /></Protected>} />
        <Route path="/fleet" element={<Protected><FleetOptionsPage /></Protected>} />
        <Route path="/profile" element={<Protected><DriverProfilePage /></Protected>} />
        {/* v2.7.56+E33.3 · Driver End-of-Day Deposit — dedicated route so
            the Dashboard tile and Profile shortcut both land on a clean
            page (avoids the react-router hash-nav quirk where hashchange
            doesn't fire on Link clicks). */}
        <Route path="/end-of-day" element={<Protected><EndOfDayPage /></Protected>} />
        {/* v2.7.58+E35 · DELIVERY SYSTEM · Section 4 · driver's sequential
            multi-stop checklist. Accessed after tapping "Continue Delivery"
            on the dashboard's Active Delivery banner. */}
        <Route path="/delivery/active/:id" element={<Protected><DeliveryActivePage /></Protected>} />
        <Route path="/earnings" element={<Protected><DriverEarningsPage /></Protected>} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminProtected><AdminDashboardPage /></AdminProtected>} />
        {/* v2.7.63+E48 · Seamless 4-Window Grid Console + Live-Map-Only pop-out
            routes. Both are AdminProtected but strip the sidebar/nav shell
            so the PWA "standalone" launch feels like a native app. */}
        <Route path="/admin/console" element={<AdminProtected><ConsoleGridPage /></AdminProtected>} />
        <Route path="/admin/live-map-only" element={<AdminProtected><LiveMapOnlyPage /></AdminProtected>} />
        {/* Update 4 · Detached TAXI Calculator window — calculator-only,
            stays under the admin session, never shows the console/booking form. */}
        <Route path="/admin/taxi-calculator" element={<AdminProtected><TaxiCalculatorPopupPage /></AdminProtected>} />
        <Route path="/admin/console-sessions" element={<AdminProtected><ConsoleSessionsPage /></AdminProtected>} />
        <Route path="/dev-license" element={<AdminProtected><DevLicensePage /></AdminProtected>} />
        <Route path="/admin/system-health" element={<AdminProtected><SystemHealthPage /></AdminProtected>} />
        <Route path="/admin/weather" element={<AdminProtected><WeatherCommanderPage /></AdminProtected>} />
        <Route path="/admin/state-emergencies" element={<AdminProtected><StateEmergenciesPage /></AdminProtected>} />
        <Route path="/admin/fleet/driver-safety" element={<AdminProtected><DriverSafetyPage /></AdminProtected>} />
        {/* v2.8+E314 · DOT Compliance System disabled — HOS Audit + DOT
            Compliance (Driver Time Control) routes redirect to the dashboard
            so old bookmarks stop working. Pages + DB data retained in code. */}
        <Route path="/admin/fleet/hos-audit" element={<Navigate to="/admin" replace/>} />
        <Route path="/admin/fleet/driver-time-control" element={<Navigate to="/admin" replace/>} />
        <Route path="/admin/weather/outages" element={<AdminProtected><ActivePowerOutagesPage /></AdminProtected>} />
        <Route path="/admin/weather/cms" element={<AdminProtected><WeatherCMSPage /></AdminProtected>} />
        <Route path="/admin/developer/dispatch-video-wall" element={<AdminProtected><DispatchVideoWallPage /></AdminProtected>} />
        <Route path="/admin/developer/kiosk-links" element={<AdminProtected><KioskLinksPage /></AdminProtected>} />
        <Route path="/admin/developer/palette-debug" element={<AdminProtected><PaletteDebugPage /></AdminProtected>} />
        <Route path="/admin/email" element={<AdminProtected><EmailClientPage /></AdminProtected>} />
        <Route path="/admin/developer-email" element={<AdminProtected><DeveloperEmailAdminPage /></AdminProtected>} />
        <Route path="/admin/chat" element={<AdminProtected><StaffChatPanel /></AdminProtected>} />
        <Route path="/admin/helpdesk" element={<AdminProtected><HelpdeskPage /></AdminProtected>} />
        <Route path="/admin/api-vault" element={<AdminProtected><ApiVaultPage /></AdminProtected>} />
        <Route path="/admin/calendar" element={<AdminProtected><CalendarPage /></AdminProtected>} />
        <Route path="/admin/future-fares" element={<AdminProtected><FutureFaresPage /></AdminProtected>} />
        {/* v2.7.65+E118 · Knowledge Center Wiki + PDF manual compile */}
        <Route path="/admin/wiki" element={<AdminProtected><KnowledgeCenterPage /></AdminProtected>} />
        {/* v2.7.65+E133 · Public-facing Knowledge Center deep-links
            (staff-authed via AdminProtected — dispatch/field-supervisor/driver
            all pass the L1+ audience filter server-side). */}
        <Route path="/knowledge-center"                  element={<AdminProtected><KnowledgeCenterPage /></AdminProtected>} />
        <Route path="/knowledge-center/articles/:slug"   element={<AdminProtected><KnowledgeCenterPage /></AdminProtected>} />
        {/* v2.7.65+E128 · App Management Portal (super_user / general_partner / dispatch) */}
        <Route path="/admin/app-management-portal" element={<AdminProtected><AppManagementPortalPage /></AdminProtected>} />
        {/* v2.7.65+E129 · Driver App Manager · Leaflet Zone Editor (L5 only) */}
        <Route path="/admin/driver-app-manager" element={<AdminProtected><DriverAppManagerPage /></AdminProtected>} />
        {/* v2.7.65+E132 · Community Voucher Program */}
        <Route path="/voucher-program" element={<VoucherProgramPage />} />
        <Route path="/my-vouchers"     element={<VoucherLedgerPage />} />  {/* v2.7.65+E133 */}
        <Route path="/admin/vouchers" element={<AdminProtected><VoucherAdminPage /></AdminProtected>} />
        {/* v2.7.65+E138 · Manage Emails · General Partner mailbox hub */}
        <Route path="/admin/email/manage" element={<AdminProtected><ManageEmailsPage /></AdminProtected>} />
        <Route path="/admin/rate-table" element={<AdminProtected><RateTableAdminPage /></AdminProtected>} />
        <Route path="/admin/ride-coordinator" element={<AdminProtected><RideCoordinatorPage /></AdminProtected>} />
        <Route path="/admin/rider-hours" element={<AdminProtected><RiderHoursPage /></AdminProtected>} />
        {/* v2.7.50+E26 Phase 1 · Master Access — page-level owner gate inside MasterAccessPage */}
        <Route path="/admin/master-access" element={<AdminProtected><MasterAccessPage /></AdminProtected>} />
        {/* v2.7.51+E27 Storage Portal Phase 2 · Dropbox - Business
            v2.7.65+E138 · Suppressed. When REACT_APP_FEATURE_DROPBOX_ENABLED is
            not "true" the route silently bounces to /admin/dashboard so old
            bookmarks stop working and no worker tasks fire. */}
        <Route
          path="/admin/dropbox"
          element={
            String(process.env.REACT_APP_FEATURE_DROPBOX_ENABLED || "").toLowerCase() === "true"
              ? <AdminProtected><DropboxPage /></AdminProtected>
              : <Navigate to="/admin/dashboard" replace/>
          }
        />
        {/* Catch-all for any deeper /admin/dropbox/... path */}
        <Route path="/admin/dropbox/*" element={<Navigate to="/admin/dashboard" replace/>} />
        {/* v2.7.64+E49 · Training Center (standalone group beneath File Sharing) */}
        <Route path="/admin/training-center/wiki" element={<AdminProtected><TrainingWikiPage /></AdminProtected>} />
        <Route path="/admin/training-center/lms" element={<AdminProtected><TrainingLmsPage /></AdminProtected>} />
        <Route path="/admin/training-center/kmc" element={<AdminProtected><TrainingKmcPage /></AdminProtected>} />
        <Route path="/admin/contracts/new" element={<AdminProtected><NewContractPage /></AdminProtected>} />
        <Route path="/admin/contracts/:classificationSlug" element={<AdminProtected><ContractsListPage /></AdminProtected>} />
        <Route path="/admin/contracts/:classificationSlug/:contract_id" element={<AdminProtected><ContractDetailPage /></AdminProtected>} />
        <Route path="/local-weather" element={<LocalWeatherPage />} />
        <Route path="/weather" element={<LocalWeatherPage />} />
        <Route path="/kiosk/wall" element={<KioskWallPage />} />
        {/* v2.6.7 batch I · Portal hidden behind PORTAL_ENABLED flag. */}
        {PORTAL_ENABLED && (
          <Route path="/portal" element={<AdminProtected><AdminGate><PortalPage /></AdminGate></AdminProtected>} />
        )}
        <Route path="/ride/login" element={<CustomerLoginPage />} />
        <Route path="/ride" element={<CustomerProtected><CustomerDashboard /></CustomerProtected>} />
        {/* /business + /business/login removed v2.6.5 — UI hidden but backend
           endpoints + historical data remain intact so the Rides/Waybills
           tab still renders past business rides. */}
        <Route path="/business/*" element={<Navigate to="/login" replace />} />
        <Route path="/group" element={<Navigate to="/city" replace />} />
        <Route path="/legal/:kind" element={<LegalPage />} />
        <Route path="/legal" element={<Navigate to="/legal/privacy" replace />} />
        {/* v2.6.8 batch M · public driver verification page. Riders open this
            to confirm OSBI / DMV attestations and view the driver's per-vehicle
            taxi cab license. No auth required. */}
        <Route path="/verify/driver/:driverId" element={<DriverVerificationPage />} />
        {/* v2.6.9 batch Q · Driver standalone TAXIcab Incident Commander page */}
        <Route path="/incident-report" element={<Protected><IncidentReportPage /></Protected>} />
      </Routes>
      {/* v2.6.9 batch CD · Global cookie-consent banner. Mounts on every
          route inside BrowserRouter so the embedded <Link> to /legal/privacy
          works. Self-suppresses once accepted; on opt-out, clears storage
          and hard-redirects to google.com. */}
      <CookieConsentBanner />
      {/* v2.7.44 · Persistent compliance footer — mounts on every /admin/*
          path so EVERY admin view (dashboard, contracts detail, weather,
          palette debug, etc.) shows the corporate copyright + ownership
          notice at the absolute bottom. */}
      <AdminFooterMount />
    </BrowserRouter>
  );
}

function AdminFooterMount() {
  const loc = useLocation();
  // v2.7.65+E163 · Pre-Shift Sound Check must NEVER appear on public /
  // sign-in surfaces. AdminFooter carries the Pre-Shift Sound Check
  // strip + personal alarm-volume controls, so we now gate the mount
  // on TWO conditions:
  //   1. Path is under /admin AND is NOT the /admin/login or /admin/gate
  //      auth surfaces (or their sub-paths).
  //   2. A valid admin bearer token is present in localStorage. Anyone
  //      who is not signed in (customers, visitors, driver logins) never
  //      sees the footer even if they somehow land on an /admin URL.
  // Both conditions must hold — belt-and-suspenders. The footer + its
  // sound-check buttons remain fully available to signed-in team members
  // on every other /admin/* view (dashboard, contracts, weather, etc.).
  const path = loc.pathname || "";
  if (!path.startsWith("/admin")) return null;
  if (path === "/admin/login" || path.startsWith("/admin/login/")) return null;
  if (path === "/admin/gate"  || path.startsWith("/admin/gate/"))  return null;
  let hasAdminToken = false;
  try { hasAdminToken = !!window.localStorage.getItem("rtg_admin_token"); }
  catch { hasAdminToken = false; }
  if (!hasAdminToken) return null;
  return <AdminFooter />;
}

export default function App() {
  return (
    <div className="App min-h-screen bg-white text-slate-900 dark:bg-[#0c0d10] dark:text-zinc-100" style={{ fontFamily: "Manrope, system-ui, sans-serif" }}>
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <filter id="black-to-alpha" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  1 1 1 0 0"/>
          </filter>
        </defs>
      </svg>
      <AuthProvider>
        <AdminProvider>
          <CustomerProvider>
            <ConfigProvider>
              <ThemeProvider>
                <SidebarToggleProvider>
                  <AppShell />
                </SidebarToggleProvider>
              </ThemeProvider>
            </ConfigProvider>
          </CustomerProvider>
        </AdminProvider>
      </AuthProvider>
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}
